import { IntlShape } from 'react-intl';
import { platformName } from '../platformMeta';
import { SocialPlatform } from '../types';
import { ConnectReason } from '../hooks/useConnectReturn';

/**
 * Why a login did not finish, said as a sentence the artist can act on.
 *
 * Facebook and Instagram fail the same check for different reasons, so
 * `noTargets` reads differently for each: one needs a Page, the other needs a
 * professional account linked to a Page.
 */
export function connectErrorMessage(intl: IntlShape, platform: SocialPlatform, reason: ConnectReason): string {
  const name = platformName(platform);

  switch (reason) {
    case 'denied':
      return intl.formatMessage({
        id: 'app.sml.promote.channel.connect.error.denied',
        defaultMessage: 'You cancelled the login.',
      });
    case 'invalidState':
      return intl.formatMessage({
        id: 'app.sml.promote.channel.connect.error.invalidState',
        defaultMessage: 'The login took too long. Try again.',
      });
    case 'exchangeFailed':
      return intl.formatMessage(
        {
          id: 'app.sml.promote.channel.connect.error.exchangeFailed',
          defaultMessage: '{platform} did not accept the login. Try again.',
        },
        { platform: name }
      );
    case 'notReviewed':
      return intl.formatMessage(
        {
          id: 'app.sml.promote.channel.connect.error.notReviewed',
          defaultMessage:
            'Meta has not reviewed MUSIC24 for {platform} yet. Add your account as a tester in the Meta app, or wait for the review.',
        },
        { platform: name }
      );
    case 'notConfigured':
      return intl.formatMessage(
        {
          id: 'app.sml.promote.channel.connect.error.notConfigured',
          defaultMessage: 'Auto-posting is not set up for {platform} yet.',
        },
        { platform: name }
      );
    // `noInstagramAccount` reads as the Instagram `noTargets` line here. The banner has its own
    // text for it when the server offers the sign-in of Instagram (`ConnectResultAlert`).
    case 'noInstagramAccount':
    case 'noTargets':
      if (platform === 'facebook') {
        return intl.formatMessage({
          id: 'app.sml.promote.channel.connect.error.noTargets.facebook',
          defaultMessage: 'The login has no Facebook Page to post to. Create a Page first.',
        });
      }
      if (platform === 'instagram') {
        return intl.formatMessage({
          id: 'app.sml.promote.channel.connect.error.noTargets.instagram',
          defaultMessage: 'No Instagram professional account is linked to your Facebook Pages.',
        });
      }
      return intl.formatMessage(
        {
          id: 'app.sml.promote.channel.connect.error.noTargets',
          defaultMessage: 'The login has no {platform} account we can post to.',
        },
        { platform: name }
      );
    case 'unknown':
    default:
      return intl.formatMessage(
        {
          id: 'app.sml.promote.channel.connect.error.unknown',
          defaultMessage: 'The {platform} login did not finish. Try again.',
        },
        { platform: name }
      );
  }
}
